import { FaroTextButton } from "@components/common/faro-text-button";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useMarkupContext } from "@context-providers/markup/markup-context";
import { BaseProjectIdProps } from "@custom-types/base-types";
import { Markup } from "@custom-types/project-markups-types";
import { blue } from "@flat-ui";
import { useProjectMarkupUpdate } from "@hooks/project-markups/use-project-markup-update";
import { useToast } from "@hooks/use-toast";
import { DeleteMarkupDialog } from "@pages/project-details/project-markups/markup-delete-dialog";
import { nounPluralize } from "@utils/data-display";
import {
  BulkActionEvents,
  TableType,
} from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";

interface Props extends BaseProjectIdProps {
  /** List of markups to show in table */
  selectedMarkups: Markup[];
}

/** Show delete markup button and dispatch action where the delete dialog will act accordingly */
export function BulkDeleteMarkups({
  selectedMarkups,
  projectId,
}: Props): JSX.Element {
  const [shouldShowDialog, setShouldShowDialog] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const { bulkDeleteMarkupsFromProject } = useProjectMarkupUpdate({
    projectId,
  });
  const { showToast } = useToast();
  const { trackEvent } = useTrackEvent();
  const { handleErrorWithToast } = useErrorContext();
  const { markups, updateMarkups } = useMarkupContext();

  /** Delete markups in bulk */
  async function deleteMarkupsInBulk(): Promise<void> {
    trackEvent({
      name: BulkActionEvents.bulkDeleteMarkup,
      props: {
        dataType: TableType.projectMarkups,
        numberOfEntities: selectedMarkups.length,
      },
    });

    try {
      setIsDeleting(true);
      const response = await bulkDeleteMarkupsFromProject(selectedMarkups);
      if (response && response.failedMutations.length === 0) {
        showToast({
          message: "Annotations deleted.",
          type: "success",
        });
      } else {
        showToast({
          message: `Failed to delete ${nounPluralize({
            counter: response.failedMutations.length,
            word: "annotation",
          })}`,
          type: "error",
        });
      }
      const updatedMarkups = markups.filter(
        (markup) =>
          !response.successMutations.some(
            (element) => element.elementId === markup.id
          )
      );
      updateMarkups(updatedMarkups);
    } catch (error) {
      handleErrorWithToast({
        id: `Delete annotation-${Date.now().toString()}`,
        title: "Failed to delete the annotations.",
        error,
      });
    } finally {
      setIsDeleting(false);
      setShouldShowDialog(false);
    }
  }
  return (
    <>
      <FaroTextButton
        onClick={() => setShouldShowDialog(true)}
        tooltipText="Delete annotations"
        sx={{
          fontWeight: 600,
          paddingX: "10px",
          paddingY: "8px",
          width: "60px",
          color: blue[500],
        }}
      >
        Delete
      </FaroTextButton>

      {shouldShowDialog && (
        <DeleteMarkupDialog
          dialogContent={{
            title: "Delete annotations?",
            description:
              "This will remove the selected annotations from your project.",
          }}
          onClose={() => setShouldShowDialog(false)}
          onConfirm={deleteMarkupsInBulk}
          isDeleting={isDeleting}
        />
      )}
    </>
  );
}
